import {
    AfterViewInit,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    HostBinding,
    HostListener,
    InjectionToken,
    inject
} from "@angular/core";
import { ISnackbarButton, ISnackbarOptions } from "./lg-snackbar.types";
import { OverlayRef } from "@angular/cdk/overlay";
import { LgTranslateService } from "@logex/framework/lg-localization";
import { animate, AnimationEvent, state, style, transition, trigger } from "@angular/animations";

import { LgIconComponent } from "../controls";
import { LgButtonComponent } from "../controls/lg-button.component";

export const LG_SNACKBAR_OPTIONS = new InjectionToken<ISnackbarOptions>("LgSnackbarOptions");

const LG_SNACKBAR_ANIMATION_MAPPER = {
    left: "translateX(-100%)",
    right: "translateX(100%)",
    top: "translateY(-100%)",
    bottom: "translateY(100%)"
};

@Component({
    selector: "lg-snackbar",
    templateUrl: "./lg-snackbar.component.html",
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: { "[style.flex-grow]": "1" },
    imports: [LgIconComponent, LgButtonComponent],
    animations: [
        trigger("basicAnimation", [
            state("closed", style({ opacity: 0 })),
            transition("* => opened", [
                style({ transform: "{{openAnimation}}" }),
                animate("250ms ease-out")
            ]),
            transition("* => closed", animate(250))
        ]),
        trigger("moveUpAnimation", [
            transition("* => moved", [
                style({ transform: "translateY({{ moveUpOffset }}px)" }),
                animate("250ms ease-out")
            ])
        ])
    ]
})
export class LgSnackbarComponent implements AfterViewInit {
    options = inject(LG_SNACKBAR_OPTIONS);
    private _overlayRef = inject(OverlayRef);
    private _translate = inject(LgTranslateService);
    private _changeDetectorRef = inject(ChangeDetectorRef);

    @HostBinding("@basicAnimation")
    get _animationBasicState(): {
        value: "opened" | "closed";
        params: { openAnimation: string };
    } {
        return {
            value: this._visibilityState,
            params: {
                openAnimation: LG_SNACKBAR_ANIMATION_MAPPER[this._animateFrom]
            }
        };
    }

    @HostListener("@basicAnimation.done", ["$event"])
    _animationStateDone(event: AnimationEvent): void {
        if (event.fromState === "opened" && event.toState === "closed") {
            this._overlayRef.detach();
        }
    }

    // ---------------------------------------------------------------------------------------------
    @HostBinding("@moveUpAnimation")
    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    get _animationMoveUpState() {
        return {
            value: this._moveUpState,
            params: {
                moveUpOffset: this._moveUpOffset
            }
        };
    }

    @HostListener("@moveUpAnimation.done", ["$event"])
    _animationMoveUpDone(event: AnimationEvent): void {
        if (event.toState === "moved") {
            this._moveUpState = "initial";
        }
    }

    // ---------------------------------------------------------------------------------------------
    _visibilityState: "opened" | "closed" = "closed";

    _moveUpState: "initial" | "moved" = "initial";
    _moveUpOffset: number | undefined;

    _title: string | undefined;
    _text: string | undefined;
    _animateFrom: "left" | "right" | "top" | "bottom" = "left";

    constructor() {
        this._title = this.options.titleLc
            ? this._translate.translate(this.options.titleLc)
            : this.options.title;

        this._text = this.options.textLc
            ? this._translate.translate(this.options.textLc)
            : this.options.text;

        this._animateFrom =
            this.options.horizontalPosition === "center"
                ? (this.options.verticalPosition ?? "bottom")
                : (this.options.horizontalPosition ?? "left");
    }

    ngAfterViewInit(): void {
        requestAnimationFrame(() => {
            this._visibilityState = "opened";
            this._changeDetectorRef.markForCheck();
        });
    }

    hide(): void {
        this._visibilityState = "closed";
        this._changeDetectorRef.markForCheck();
    }

    _onButtonClick(button: ISnackbarButton): void {
        button.callback().then(close => {
            if (close) this.hide();
        });
    }

    moveUp(offset: number): void {
        this._moveUpOffset = offset;
        this._moveUpState = "moved";
        this._changeDetectorRef.markForCheck();
    }
}
